import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { cartData } from "../store/cart-store";
import { retailSubtotal, totalSavings } from "../store/retail-price-store";

/**
 * <cart-savings>
 *
 * Retail subtotal and "You save" rows shown above the cart subtotal. Renders
 * nothing when the cart has no savings, so undiscounted carts never show a
 * "$0.00 saved" line.
 */
@customElement("cart-savings")
export class CartSavings extends LitElement {
  @state() private _retail = 0;
  @state() private _savings = 0;
  @state() private _currency = "USD";

  private _disposeEffect?: () => void;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this._disposeEffect = effect(() => {
      this._retail = retailSubtotal.value;
      this._savings = totalSavings.value;
      this._currency = cartData.value?.currency ?? "USD";
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
  }

  private _format(cents: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: this._currency,
    }).format(cents / 100);
  }

  render() {
    if (this._savings <= 0) return nothing;

    return html`
      <div class="flex flex-col gap-1 text-sm">
        <div
          class="flex items-center justify-between text-[var(--color-neutral-600)]"
        >
          <span>Retail price</span>
          <span class="line-through">${this._format(this._retail)}</span>
        </div>
        <div
          class="flex items-center justify-between font-bold text-[var(--color-success-700)]"
        >
          <span>You save</span>
          <span>−${this._format(this._savings)}</span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cart-savings": CartSavings;
  }
}
